import type { EditableColumnDef, EditableRow, ValidateCellFn } from '../types';

export function isDraftRowEmpty(row: EditableRow): boolean {
  return Object.values(row.fields).every(v => v.trim() === '');
}

/**
 * True when the add-row draft is empty or passes `validateCell` for every column.
 */
export function isDraftRowValid(
  draftRow: EditableRow,
  columns: EditableColumnDef[],
  validateCell?: ValidateCellFn,
  isDraftEmpty: (row: EditableRow) => boolean = isDraftRowEmpty,
  peerRows: EditableRow[] = []
): boolean {
  if (!validateCell) {
    return true;
  }
  if (isDraftEmpty(draftRow)) {
    return true;
  }
  for (const col of columns) {
    const msg = validateCell({
      row: draftRow,
      column: col,
      value: draftRow.fields[col.id] ?? '',
      peerRows,
    });
    if (msg) {
      return false;
    }
  }
  return true;
}

/**
 * Returns whether every committed row and the add-row draft pass `validateCell`.
 * Pending-delete rows are skipped. An empty draft (all fields blank) is valid.
 *
 * @param skipDraftRowValidation — When true, only committed rows are validated (e.g. while a
 *   committed row is expanded so the user can edit/save it without the add-row draft blocking).
 */
export function evaluateEditableTableValid(
  rows: EditableRow[],
  draftRow: EditableRow,
  columns: EditableColumnDef[],
  validateCell?: ValidateCellFn,
  isDraftEmpty: (row: EditableRow) => boolean = isDraftRowEmpty,
  skipDraftRowValidation = false
): boolean {
  if (!validateCell) {
    return true;
  }
  for (const row of rows) {
    if (row.pendingDelete) {
      continue;
    }
    for (const col of columns) {
      const msg = validateCell({
        row,
        column: col,
        value: row.fields[col.id] ?? '',
        peerRows: rows,
      });
      if (msg) {
        return false;
      }
    }
  }
  if (
    !skipDraftRowValidation &&
    !isDraftEmpty(draftRow) &&
    !isDraftRowValid(draftRow, columns, validateCell, isDraftEmpty, rows)
  ) {
    return false;
  }
  return true;
}
